import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Award,
  BarChart3,
  Brain,
  Briefcase,
  DollarSign,
  FileText,
  FlaskConical,
  Gavel,
  GitCompare,
  Landmark,
  LayoutDashboard,
  Newspaper,
  Plug,
  Radio,
  Scale,
  Search,
  Sparkles,
  Terminal,
  TrendingUp,
  Users,
  Video,
  Workflow,
} from "lucide-react";

export type RouteKind = "core" | "workbench" | "redirect";

export interface AppRouteDefinition {
  key: string;
  label: string;
  section: "core" | "analysis" | "operations";
  canonicalPath: string;
  aliases: string[];
  kind: RouteKind;
  icon: LucideIcon;
}

export const APP_ROUTES: AppRouteDefinition[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    section: "core",
    canonicalPath: "/",
    aliases: ["/index.html"],
    kind: "core",
    icon: LayoutDashboard,
  },
  {
    key: "portfolio",
    label: "Portfolio",
    section: "core",
    canonicalPath: "/portfolio",
    aliases: [],
    kind: "core",
    icon: Briefcase,
  },
  {
    key: "command-center",
    label: "Command Center",
    section: "core",
    canonicalPath: "/command-center",
    aliases: ["/command-center.html", "/mobile/command-center.html"],
    kind: "core",
    icon: Terminal,
  },
  {
    key: "market-explorer",
    label: "Market Explorer",
    section: "core",
    canonicalPath: "/market-explorer",
    aliases: ["/market-explorer.html", "/mobile/market-explorer.html"],
    kind: "core",
    icon: Search,
  },
  {
    key: "prediction-engine",
    label: "Prediction Engine",
    section: "core",
    canonicalPath: "/prediction-engine",
    aliases: ["/prediction-engine.html", "/mobile/prediction-engine.html"],
    kind: "core",
    icon: Brain,
  },
  {
    key: "signals",
    label: "Signals",
    section: "core",
    canonicalPath: "/signals",
    aliases: ["/signals.html"],
    kind: "core",
    icon: Radio,
  },
  {
    key: "trader-intelligence",
    label: "Trader Intelligence",
    section: "analysis",
    canonicalPath: "/trader-intelligence",
    aliases: ["/trader-intelligence.html", "/mobile/trader-intel.html"],
    kind: "workbench",
    icon: Users,
  },
  {
    key: "news-center",
    label: "News Center",
    section: "analysis",
    canonicalPath: "/news-center",
    aliases: ["/news-center.html"],
    kind: "workbench",
    icon: Newspaper,
  },
  {
    key: "resolution-center",
    label: "Resolution Center",
    section: "analysis",
    canonicalPath: "/resolution-center",
    aliases: ["/resolution-center.html"],
    kind: "workbench",
    icon: Scale,
  },
  {
    key: "cross-market",
    label: "Cross Market",
    section: "analysis",
    canonicalPath: "/cross-market",
    aliases: ["/cross-market.html"],
    kind: "workbench",
    icon: GitCompare,
  },
  {
    key: "ops-pulse",
    label: "Ops Pulse",
    section: "operations",
    canonicalPath: "/ops-pulse",
    aliases: ["/ops-pulse.html"],
    kind: "workbench",
    icon: Activity,
  },
  {
    key: "cost-center",
    label: "Cost Center",
    section: "operations",
    canonicalPath: "/cost-center",
    aliases: ["/cost-center.html"],
    kind: "workbench",
    icon: DollarSign,
  },
  {
    key: "reports",
    label: "Reports",
    section: "operations",
    canonicalPath: "/reports",
    aliases: ["/reports.html"],
    kind: "workbench",
    icon: FileText,
  },
  {
    key: "integrations",
    label: "Integrations",
    section: "operations",
    canonicalPath: "/integrations",
    aliases: ["/integrations.html"],
    kind: "workbench",
    icon: Plug,
  },
  {
    key: "advanced-analytics",
    label: "Advanced Analytics",
    section: "analysis",
    canonicalPath: "/advanced-analytics",
    aliases: ["/advanced-analytics.html", "/mobile/analytics.html"],
    kind: "workbench",
    icon: BarChart3,
  },
  {
    key: "analysis-demo",
    label: "Analysis Demo",
    section: "analysis",
    canonicalPath: "/analysis-demo",
    aliases: ["/analysis-demo.html"],
    kind: "workbench",
    icon: FlaskConical,
  },
  {
    key: "manifold",
    label: "Manifold",
    section: "analysis",
    canonicalPath: "/manifold",
    aliases: ["/manifold.html"],
    kind: "workbench",
    icon: TrendingUp,
  },
  {
    key: "predictit",
    label: "PredictIt",
    section: "analysis",
    canonicalPath: "/predictit",
    aliases: ["/predictit.html"],
    kind: "workbench",
    icon: Landmark,
  },
  {
    key: "fred-economic",
    label: "FRED Economic",
    section: "analysis",
    canonicalPath: "/fred-economic",
    aliases: ["/fred-economic.html"],
    kind: "workbench",
    icon: BarChart3,
  },
  {
    key: "twitter-analysis",
    label: "Twitter Analysis",
    section: "analysis",
    canonicalPath: "/twitter-analysis",
    aliases: ["/twitter-analysis.html"],
    kind: "workbench",
    icon: TrendingUp,
  },
  {
    key: "youtube-analysis",
    label: "YouTube Analysis",
    section: "analysis",
    canonicalPath: "/youtube-analysis",
    aliases: ["/youtube-analysis.html"],
    kind: "workbench",
    icon: Video,
  },
  {
    key: "alpha-signals",
    label: "Alpha Signals",
    section: "analysis",
    canonicalPath: "/alpha/signals",
    aliases: [],
    kind: "workbench",
    icon: Radio,
  },
  {
    key: "alpha-debates",
    label: "Debate Council",
    section: "analysis",
    canonicalPath: "/alpha/debates",
    aliases: [],
    kind: "workbench",
    icon: Gavel,
  },
  {
    key: "alpha-leaderboard",
    label: "Agent Leaderboard",
    section: "analysis",
    canonicalPath: "/alpha/leaderboard",
    aliases: [],
    kind: "workbench",
    icon: Award,
  },
  {
    key: "workflow-analysis",
    label: "AI Workflow",
    section: "analysis",
    canonicalPath: "/workflow-analysis",
    aliases: [],
    kind: "workbench",
    icon: Workflow,
  },
  {
    key: "prompt-evolution",
    label: "Prompt Lab",
    section: "operations",
    canonicalPath: "/prompt-evolution",
    aliases: [],
    kind: "workbench",
    icon: Sparkles,
  },
  {
    key: "validation",
    label: "Validation",
    section: "operations",
    canonicalPath: "/validation",
    aliases: [],
    kind: "workbench",
    icon: FlaskConical,
  },
];

export const ROUTE_ALIAS_MAP = APP_ROUTES.flatMap((route) => [
  route.canonicalPath,
  ...route.aliases,
]).filter(Boolean);

export function matchRoute(pathname: string): AppRouteDefinition | undefined {
  return APP_ROUTES.find(
    (route) => route.canonicalPath === pathname || route.aliases.includes(pathname),
  );
}

export function getCoreNavigation(): AppRouteDefinition[] {
  return APP_ROUTES.filter((route) => route.key !== "login");
}
